import React from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Generic page hero — one block reused for Strapi sections.home-hero and
// sections.career-hero (both = heading + description + image + CTAs). Text left,
// image right on desktop. No styled-system / next.
export const pageHeroSchema = z.object({
  heading: z.string().min(1),
  description: z.string().optional(),
  imageUrl: z.string().optional(),
  bgImageUrl: z.string().optional(),
  primaryCta: z.object({ label: z.string(), href: safeUrl }).optional(),
  secondaryCta: z.object({ label: z.string(), href: safeUrl }).optional(),
});

type PageHeroProps = z.infer<typeof pageHeroSchema>;

const Band = styled.section<{ $bg?: string }>`
  border-radius: 0 0 8rem 0;
  padding: 8rem 1.6rem 5rem;
  background: ${({ $bg }) =>
    $bg
      ? `linear-gradient(93deg, rgba(232,239,255,0.9), rgba(255,255,255,0.6)), url(${$bg}) center/cover no-repeat`
      : 'linear-gradient(93deg, #e8efff 0%, #ffffff 100%)'};

  @media (min-width: 64em) {
    padding: 10rem 2.4rem 8rem;
  }
`;

const Inner = styled.div`
  display: grid;
  gap: 3rem;
  align-items: center;
  max-width: 120rem;
  margin: 0 auto;

  @media (min-width: 64em) {
    grid-template-columns: 1.1fr 0.9fr;
    gap: 5rem;
  }
`;

const Heading = styled.h1`
  margin: 0;
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(3.2rem, 5vw, 6rem);
  line-height: 1.1;
`;

const Description = styled.p`
  margin: 1.6rem 0 0;
  max-width: 52rem;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.7rem;
  line-height: 1.7;
`;

const Ctas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem;
  margin-top: 2.4rem;
`;

const Cta = styled.a<{ $variant: 'primary' | 'secondary' }>`
  display: inline-block;
  padding: 1.2rem 2.4rem;
  border-radius: 1.6rem;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  ${({ $variant }) =>
    $variant === 'primary'
      ? 'background: #063091; color: #ffffff; border: 1px solid #063091;'
      : 'background: transparent; color: #063091; border: 1px solid #063091;'}
`;

const HeroImage = styled.img`
  width: 100%;
  object-fit: contain;
`;

function PageHero({ heading, description, imageUrl, bgImageUrl, primaryCta, secondaryCta }: PageHeroProps) {
  return (
    <Band $bg={bgImageUrl}>
      <Inner>
        <div>
          <Heading>{heading}</Heading>
          {description ? <Description>{description}</Description> : null}
          {primaryCta || secondaryCta ? (
            <Ctas>
              {primaryCta ? (
                <Cta $variant="primary" href={primaryCta.href} target="_blank" rel="noreferrer">
                  {primaryCta.label}
                </Cta>
              ) : null}
              {secondaryCta ? (
                <Cta $variant="secondary" href={secondaryCta.href}>
                  {secondaryCta.label}
                </Cta>
              ) : null}
            </Ctas>
          ) : null}
        </div>
        {imageUrl ? <HeroImage src={imageUrl} alt="" /> : null}
      </Inner>
    </Band>
  );
}

export const pageHeroBlock: BlockDefinition<PageHeroProps> = {
  type: 'pageHero',
  schema: pageHeroSchema,
  Renderer: PageHero,
};
